import React from "react";

import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faYoutube,
} from "@fortawesome/free-brands-svg-icons";
import {
  faEnvelope,
  faGlobe,
  faPhone,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  Col,
  Container,
  Image,
  ListGroup,
  Row,
} from "react-bootstrap";
import { Navigate, NavLink, useParams } from "react-router-dom";

import {
  facebookLink,
  flatPhoneNumber,
  getBaseUrl,
  instagramLink,
  linkedinLink,
  youTubeLink,
} from "@/utils/Toolbox";

import DocumentComponent from "../components/Document.component";
import i18n from "../i18n";

interface IContact {
  phone: string;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
  vcf: string;
  avatar?: string;
  description: string;
}

const VCard = (): React.JSX.Element => {
  const { t } = i18n;

  const params = useParams();
  const contactParam = (params.contact ?? "").toLowerCase();
  if (contactParam === undefined) {
    return <Navigate to="/" />;
  }

  const contacts: Record<string, IContact> = {
    parinaz_pakniat: {
      phone: "(514) 885-1361",
      email: "parinaz@pakville.ca",
      first_name: "Parinaz",
      last_name: "Pakniat",
      vcf: "/vcards/ParinazPakniat.vcf",
      title: "Founder & CEO / Fondatrice et PDG",
      avatar: "/images/story/ParinazPakniatAvatar.jpg",
      description:
        "PakVille Modular Buildings / Bâtiments modulaires de PakVille",
    },
    parinaz: {
      phone: "(514) 885-1361",
      email: "parinaz@pakville.ca",
      first_name: "Parinaz",
      last_name: "Pakniat",
      vcf: "/vcards/ParinazPakniat.vcf",
      title: "Founder & CEO / Fondatrice et PDG",
      avatar: "/images/story/ParinazPakniatAvatar.jpg",
      description:
        "PakVille Modular Buildings / Bâtiments modulaires de PakVille",
    },
    maxime: {
      phone: "(514) 553-9112",
      email: "maxime@pakville.ca",
      first_name: "Maxime",
      last_name: "Ridoux",
      vcf: "/vcards/Maxime.vcf",
      title: "Directeur des ventes",
      avatar: "/images/story/MaximeRidoux.jpg",
      description: "Votre maison de rêve, maintenant encore mieux",
    },
    shervin: {
      phone: "(647) 973-7051",
      email: "shervin@pakville.ca",
      first_name: "Shervin",
      last_name: "Shahriari",
      vcf: "/vcards/Shervin.vcf",
      title: "Director, Operations & Business Development",
      avatar: "/images/story/ShervinShahriari.jpg",
      description: "PakVille Modular Buildings",
    },
    rene: {
      phone: "(514) 817-1364",
      email: "rene@pakville.ca",
      first_name: "René",
      last_name: "Herrera",
      vcf: "/vcards/Rene.vcf",
      title: "Brand Ambassador / Ambassadeur de marque",
      description:
        "PakVille Modular Buildings / Bâtiments modulaires de PakVille",
    },
    corrine: {
      phone: "(705) 818-1458",
      email: "corrine@pakville.ca",
      first_name: "Corrine",
      last_name: "Dayman",
      vcf: "/vcards/Corrine.vcf",
      title: "Client Solutions Manager",
      avatar: "/images/story/CorrineDayman.jpg",
      description: "PakVille Modular Buildings",
    },
  };

  const contact = contacts[contactParam];
  if (contact === undefined) {
    return <Navigate to="/" />;
  }

  const fullName = `${contact.first_name} ${contact.last_name}`;
  return (
    <React.Fragment>
      <DocumentComponent title={fullName} />

      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Container>
          <Row>
            <Col
              className="text-center"
              lg={{ span: 6, offset: 3 }}
              xs={{ span: 10, offset: 1 }}
            >
              <Card className="vcard">
                <Card.Header className="p-0">
                  <Button
                    title={t("add_to_contacts")}
                    variant="primary"
                    style={{
                      position: "absolute",
                      top: "5px",
                      right: "5px",
                      padding: "0.25rem 0.5rem",
                    }}
                  >
                    <NavLink download target="_blank" to={contact.vcf}>
                      <FontAwesomeIcon icon={faPlus} />
                    </NavLink>
                  </Button>
                  {contact.avatar ? (
                    <Image
                      roundedCircle
                      alt={fullName}
                      className="mt-4"
                      src={contact.avatar}
                      style={{ maxWidth: "30%" }}
                      title={fullName}
                    />
                  ) : null}
                  <h1 className="modelHeader text-center text-light mt-4">
                    {fullName}
                  </h1>
                  <h2 style={{ fontSize: "1rem", color: "#e5e5e5" }}>
                    {contact.title}
                  </h2>

                  <div className="d-flex justify-content-around mt-4 p-2 border-top ">
                    <a
                      className="border-end"
                      href={`tel:${flatPhoneNumber(contact.phone)}`}
                      rel="noreferrer"
                      style={{ flex: "2 0" }}
                    >
                      <FontAwesomeIcon icon={faPhone} />
                      <br />
                      {t("Call")}
                    </a>

                    <a
                      href={`mailto:${contact.email}`}
                      rel="noreferrer"
                      style={{ flex: "2 0" }}
                    >
                      <FontAwesomeIcon icon={faEnvelope} />
                      <br />
                      {t("email")}
                    </a>
                  </div>
                </Card.Header>

                <Card.Body className="p-1">
                  <ListGroup className="list-group-flush">
                    <ListGroup.Item>
                      <Card.Link href={`tel:${flatPhoneNumber(contact.phone)}`}>
                        <div className="vcardInfoContainer">
                          <FontAwesomeIcon icon={faPhone} />
                          {contact.phone}
                        </div>
                      </Card.Link>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Card.Link href={`mailto:${contact.email}`}>
                        <div className="vcardInfoContainer">
                          <FontAwesomeIcon icon={faEnvelope} />
                          {contact.email}
                        </div>
                      </Card.Link>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Card.Link href="/" target="_blank">
                        <div className="vcardInfoContainer">
                          <FontAwesomeIcon icon={faGlobe} />
                          {getBaseUrl()}
                        </div>
                      </Card.Link>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Card.Link href={youTubeLink()} target="_blank">
                        <FontAwesomeIcon icon={faYoutube} />
                      </Card.Link>
                      <Card.Link href={facebookLink()} target="_blank">
                        <FontAwesomeIcon icon={faFacebook} />
                      </Card.Link>
                      <Card.Link href={instagramLink()} target="_blank">
                        <FontAwesomeIcon icon={faInstagram} />
                      </Card.Link>
                      <Card.Link href={linkedinLink()} target="_blank">
                        <FontAwesomeIcon icon={faLinkedin} />
                      </Card.Link>
                    </ListGroup.Item>

                    <ListGroup.Item>
                      <Card.Text style={{ fontSize: "14px" }}>
                        {contact.description}
                      </Card.Text>
                    </ListGroup.Item>
                  </ListGroup>
                </Card.Body>

                <Card.Footer>
                  <Button variant="primary">
                    <NavLink download target="_blank" to={contact.vcf}>
                      <FontAwesomeIcon icon={faPlus} />{" "}
                      {t("add_to_contacts")}{" "}
                    </NavLink>
                  </Button>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default VCard;
